import S from 's-js';
import { Measurement, NullableMeasurement, NullableMeasurementLike } from 'data/models/Measurement';
import { ConvertibleMeasurement, NullableConvertibleMeasurement } from 'lib/ConvertibleMeasurement';

let idGen = 1;

export const enum BindingMode {
    // READ -- ConvertedMeasurement updates live as backingMeasurement changes
    READ = 1,
    // WRITE -- ConvertedMeasurement is the active writer. It maintains its own independent value.
    WRITE = 2
};

/// <summary>
/// Create a ConvertedMeasurement, which takes a backing Measurement instance, and provides a display in an independent UOM, rounded to a specified number of decimal places.
/// 
/// -- backingMeasurement - Measurement or ConvertibleMeasurement that backs this ConvertedMeasurement
/// -- uom                - String (not observable). The UOM for this ConvertedMeasurement.
/// -- decimalPlaces      - Integer between 0 and 20 inclusive. The number of decimal places value should be rounded to
/// -- precision          - Number. Optional. An extra precision parameter to specify more fine-grained rounding. E.g. to round to nearest half unit, specify 1 for the decimal places and .5 for the precision.
///
/// ConvertedMeasurement is readable and writable.
/// For reads, backingMeasurement can be a plain Measurement instance, a ConvertibleMeasurement, or a Measurement-like object w/ Num and UOM observables.
/// For writes, backingMeasurement must be a ConvertibleMeasurement.
/// </summary>
export class ConvertedMeasurement extends NullableMeasurement {
    private _measurement_id : string;
    private _backingMeasurement : NullableMeasurementLike;
    BindingMode : () => BindingMode;

    constructor(backingMeasurement : NullableMeasurementLike, uom : string, decimalPlaces : number, precision? : number) {
        super();
        // last raw value passed to the write method. Used to prevent clobbering user edits due to rounding.
        var lastWrite = S.data<number | null>(S.sample(backingMeasurement.Num));

        // generate a unique id for each ConvertedMeasurement constructed, so we can track who wrote to a given ConvertibleMeasurement.
        var id = this._measurement_id = 'ConvertedMeasurement_' + idGen++;

        this._backingMeasurement = backingMeasurement;

        var bindingMode = this.BindingMode = function () {
            // TH: Important: We're assuming writes to the ConvertibleMeasurement are only made via one or more ConvertedMeasurements.
            // If that isn't the case, other writers need to set the LastWriter flag before they write. Any LastWriter value that doesn't start with 'ConvertedMeasurement_' will do.
            // That way other writers can update appropriately.
            if (isConvertible(backingMeasurement) && backingMeasurement.LastWriter() === id) return BindingMode.WRITE;
            return BindingMode.READ;
        };

        this.Num = function (val? : number | null) {
            if (arguments.length === 0 || val === undefined) {
                if (bindingMode() === BindingMode.WRITE) return lastWrite();
                return _convert(backingMeasurement, uom, decimalPlaces, precision);
            } else {
                if (!isConvertible(backingMeasurement)) {
                    throw new Error('Can\'t write to a ConvertedMeasurement unless its backingMeasurement is a ConvertibleMeasurement or NullableConvertibleMeasurement');
                }
                lastWrite(val);
                if (val !== null && isNaN(val)) val = null;

                // TH: 4/25/2016 -- We used to do the check for a genuine value change before setting LastWriter.
                // But that causes problems for MeasurementInFeetAndInches instances, b/c there the internal composition of the field (its split into Feet and Inches)
                // can change w/o its overall value changing (e.g. if the field goes from { feet: 0, inches: 0 } to { feet: 0, inches: null } the overall value remains 0).
                // The overall effect was that updates weren't getting picked up when nulling out the feet and inches side of a feetinchesmetersedit or toggleablemeasurement.
                // 
                // (Original comment) For writes, we require that backingMeasurement be a ConvertibleMeasurement
                // Set LastWriter first. This protects this ConvertedMeasurement from modifications due to rounding.
                backingMeasurement.LastWriter(id);

                // TH: 4/15/2016 -- Bugfix. Don't write to the backingMeasurement unless the numeric value of this ConvertibleMeasurement is changing.
                // That prevents accidental changes caused by tabbing through a field or using arrow keys w/ in a field.
                // This comes up when there are multiple displays for the same backingMeasurement w/ a precision mismatch between the displays
                if (val !== S.sample(() => _convert(backingMeasurement, uom, decimalPlaces, precision))) {
                    // TH: No rounding on writes. backingMeasurement may acquire many decimal places.
                    // For now, my thought is that we should always plan to round to the appropriate decimal places at display time.
                    // Can revise if that presents problems.
                    let tmp = new NullableMeasurement({ Num: val, UOM: uom }),
                        backingNum = S.sample(tmp.convert(S.sample(backingMeasurement.UOM)!).Num);
                    if (backingNum !== null || backingMeasurement instanceof NullableConvertibleMeasurement) {
                        (backingMeasurement as NullableConvertibleMeasurement).Num(backingNum);
                    }
                }

                return val;
            }
        };

        // UOM doesn't change.
        this.UOM = () => uom;
    }

    set(m : Measurement) {
        throw new Error("Cannot set a ConvertedMeasurement");
    }

    // If this ConvertedMeasurement currently has LastWriter, then set LastWriter to null
    RelinquishWriter() {
        if (isConvertible(this._backingMeasurement) && S.sample(this._backingMeasurement.LastWriter) === this._measurement_id)
            this._backingMeasurement.LastWriter(null);
    }
}

function _convert(m : NullableMeasurementLike, uom : string, places : number, precision : number | undefined) {
    var out = new NullableMeasurement({ Num : m.Num(), UOM: m.UOM() }).convert(uom);
    if (precision !== undefined) out = out.roundToNearest(precision);
    out = out.roundToNPlaces(places);
    return S.sample(out.Num);
}

function isConvertible(
    measurement : NullableMeasurementLike
) : measurement is ConvertibleMeasurement | NullableConvertibleMeasurement {
    return measurement instanceof ConvertibleMeasurement || measurement instanceof NullableConvertibleMeasurement;
}



// WEBPACK FOOTER //
// ./lib/ConvertedMeasurement.ts